/**
 * Supabase 客戶端配置
 * 用於前端與 Supabase 資料庫互動
 */

import { createClient } from '@supabase/supabase-js';

// Supabase 專案設定
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// 驗證環境變數
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('缺少 Supabase 環境變數。請檢查 .env.local 檔案');
}

// 建立 Supabase 客戶端（前端使用，使用 anon key）
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true, // 啟用會員登入 session 持久化
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  db: {
    schema: 'public',
  },
});

/**
 * 查詢產品資訊
 * @param {string} sku - 產品 SKU
 * @returns {Promise<Object>} 產品資訊
 */
export async function getProduct(sku) {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('sku', sku)
    .eq('is_active', true)
    .single();

  if (error) {
    console.error('查詢產品失敗:', error);
    throw new Error('無法取得產品資訊');
  }

  return data;
}

/**
 * 查詢所有有效產品
 * @returns {Promise<Array>} 產品列表
 */
export async function getProducts() {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('is_active', true)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('查詢產品列表失敗:', error);
    throw new Error('無法取得產品列表');
  }

  return data;
}

/**
 * 驗證折扣碼
 * @param {string} code - 折扣碼
 * @returns {Promise<Object>} 折扣碼資訊
 */
export async function validateDiscountCode(code) {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('discount_codes')
    .select('*')
    .eq('code', code.toUpperCase())
    .eq('is_active', true)
    .gte('valid_until', now)
    .lte('valid_from', now)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // 找不到折扣碼
      return null;
    }
    console.error('驗證折扣碼失敗:', error);
    throw new Error('無法驗證折扣碼');
  }

  // 檢查使用次數限制
  if (data.usage_limit && data.usage_count >= data.usage_limit) {
    throw new Error('此折扣碼已達使用上限');
  }

  return data;
}

/**
 * 查詢訂單（需要 email 驗證）
 * @param {string} orderNumber - 訂單編號
 * @param {string} email - 收件人 Email
 * @returns {Promise<Object>} 訂單資訊
 */
export async function getOrder(orderNumber, email) {
  const { data, error } = await supabase
    .from('orders')
    .select(`
      *,
      payment_transactions (
        transaction_id,
        payment_type,
        status,
        pay_time
      )
    `)
    .eq('order_number', orderNumber)
    .eq('recipient_email', email)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('查詢訂單失敗:', error);
    throw new Error('無法查詢訂單');
  }

  return data;
}

/**
 * 檢查產品庫存
 * @param {string} sku - 產品 SKU
 * @param {number} quantity - 需要的數量
 * @returns {Promise<boolean>} 庫存是否充足
 */
export async function checkStock(sku, quantity) {
  const product = await getProduct(sku);
  return product.stock >= quantity;
}

export default supabase;
